import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type JobStatus = 'draft' | 'published' | 'archived';
export type ApplicationStatus =
  | 'new'
  | 'reviewed'
  | 'contacted'
  | 'hired'
  | 'rejected';

export interface JobPosting {
  id: string;
  created_at: string;
  updated_at: string;
  title: string;
  slug: string;
  short_description: string | null;
  description_md: string;
  schedule: string | null;
  pay_range: string | null;
  status: JobStatus;
  published_at: string | null;
  closes_at: string | null;
  photo_path: string | null;
  video_path: string | null;
}

export interface JobApplication {
  id: string;
  created_at: string;
  job_posting_id: string;
  applicant_name: string;
  applicant_email: string;
  applicant_phone: string | null;
  message: string | null;
  cv_storage_path: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  status: ApplicationStatus;
}

// ─── PUBLIC : postes publiés seulement ──────────────────────────────
export function usePublicJobs() {
  return useQuery({
    queryKey: ['jobs', 'public'],
    queryFn: async (): Promise<JobPosting[]> => {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('status', 'published')
        .order('published_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as JobPosting[];
    },
  });
}

export function usePublicJobBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ['jobs', 'public', 'slug', slug],
    queryFn: async (): Promise<JobPosting | null> => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .maybeSingle();
      if (error) throw error;
      return (data as JobPosting | null) ?? null;
    },
    enabled: !!slug,
  });
}

// ─── ADMIN : tous les postes (toutes statuts) ───────────────────────
export function useAllJobs() {
  return useQuery({
    queryKey: ['jobs', 'admin'],
    queryFn: async (): Promise<JobPosting[]> => {
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as JobPosting[];
    },
  });
}

export function useJobById(id: string | undefined) {
  return useQuery({
    queryKey: ['jobs', 'admin', 'id', id],
    queryFn: async (): Promise<JobPosting | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('job_postings')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data as JobPosting | null) ?? null;
    },
    enabled: !!id,
  });
}

type JobUpsert = Omit<JobPosting, 'id' | 'created_at' | 'updated_at'> & {
  id?: string;
};

export function useUpsertJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (job: JobUpsert): Promise<JobPosting> => {
      const payload: Record<string, unknown> = { ...job };
      // Auto-set published_at quand on passe au statut published
      if (job.status === 'published' && !job.published_at) {
        payload.published_at = new Date().toISOString();
      }
      const { data, error } = await supabase
        .from('job_postings')
        .upsert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as JobPosting;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('job_postings').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}

// ─── APPLICATIONS ───────────────────────────────────────────────────

// Compte total des candidatures avec status='new' (toutes postes confondus).
// Utilisé pour le badge rouge dans la sidebar admin. Re-fetch périodique
// pour que le badge apparaisse rapidement quand une candidature arrive.
export function useNewApplicationsCount() {
  return useQuery({
    queryKey: ['jobs', 'applications', 'new-count'],
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('job_applications')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'new');
      if (error) throw error;
      return count ?? 0;
    },
    refetchInterval: 60_000, // toutes les minutes
    refetchOnWindowFocus: true,
  });
}

// Compte par poste — pour afficher un badge sur chaque row admin.
export function useNewApplicationsCountByJob() {
  return useQuery({
    queryKey: ['jobs', 'applications', 'new-count-by-job'],
    queryFn: async (): Promise<Map<string, number>> => {
      const { data, error } = await supabase
        .from('job_applications')
        .select('job_posting_id')
        .eq('status', 'new');
      if (error) throw error;
      const map = new Map<string, number>();
      for (const row of data ?? []) {
        const id = (row as { job_posting_id: string }).job_posting_id;
        map.set(id, (map.get(id) ?? 0) + 1);
      }
      return map;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useJobApplications(jobPostingId: string | undefined) {
  return useQuery({
    queryKey: ['jobs', 'applications', jobPostingId],
    queryFn: async (): Promise<JobApplication[]> => {
      if (!jobPostingId) return [];
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_posting_id', jobPostingId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as JobApplication[];
    },
    enabled: !!jobPostingId,
  });
}

export interface CreateApplicationInput {
  job_posting_id: string;
  applicant_name: string;
  applicant_email: string;
  applicant_phone?: string;
  message?: string;
  cv_storage_path?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

export function useCreateApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateApplicationInput): Promise<{ id: string }> => {
      // On génère l'UUID côté client pour éviter le .select() qui
      // bloquait l'insert anon : la policy SELECT sur job_applications
      // est admin-only, donc le retour de ligne après INSERT échoue
      // pour les visiteurs publics.
      const id = crypto.randomUUID();
      const { error } = await supabase
        .from('job_applications')
        .insert({ ...input, id });
      if (error) throw error;

      // Fire-and-forget notification email (Edge Function). On n'attend
      // pas la réponse pour ne pas bloquer le user — l'email est un
      // nice-to-have, l'insert en DB est la source de vérité.
      void supabase.functions.invoke('send-job-application', {
        body: { application_id: id },
      });

      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs', 'applications'] });
    },
  });
}

export function useUpdateApplicationStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: ApplicationStatus;
    }): Promise<JobApplication> => {
      const { data, error } = await supabase
        .from('job_applications')
        .update({ status: input.status })
        .eq('id', input.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as JobApplication;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs', 'applications'] });
    },
  });
}
